import { FavoritStern, type FavoritEintrag } from '../favoriten/Favoriten';
import React, { useMemo, useState } from 'react';
import {
  Calendar,
  Clock,
  MessageSquare,
  Phone,
  PhoneOff,
  Check,
  UserPlus,
  UserX,
  Trash2,
  Clock3,
  CalendarX2,
  CalendarClock,
  XCircle,
  Send,
  Plus,
} from 'lucide-react';
import { formatDate, formatTimeRangeFromStrings } from '../../lib/dateFormat';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { type TrialLessonDTO, type TrialStatus } from '../../lib/trials';
import type { TrialWorkflowResult, TrialWorkflowActionId, TrialWorkflowState } from '../../hooks/useTrialLessonWorkflow';
import { useTrialOfferHonorarMissing } from '../../lib/trialOfferHonorar';

type Props = {
  trial: TrialLessonDTO;
  subjectLabel?: string;
  dashboardCompact?: boolean;

  // Quick actions (statusabhängig; Parent entscheidet, welche sichtbar/aktiv)
  onReschedule?: () => void;
  onCancel?: () => void;
  onMarkOccurred?: () => void;
  onResendConfirmation?: () => void;
  onNoOccurrence?: () => void;
  onEnrollment?: () => void;
  onConsiderUntil?: () => void;
  onNotStarted?: () => void;
  /** „Anmeldung löschen“ — nur solange kein Vertragskonto läuft (T-0170). */
  onDeleteEnrollment?: () => void;
  /** „Unterricht absagen“ vor dem ersten Unterricht (T-0072). */
  onCancelTeaching?: () => void;
  onOrderConfirm?: () => void;
  onSendOffer?: () => void;
  onStartCourse?: () => void;
  onReminder?: () => void;
  onEmail?: () => void;
  onOpenHistory?: () => void;
  onAddQuickNote?: (note: string) => Promise<void> | void;
  onContactAction?: (channel: 'phone' | 'call_failed' | 'email') => Promise<void> | void;
  onToggleFavorite?: () => void;
  isFavorite?: boolean;
  /** Wer die Probestunde vorgemerkt hat (T-0014). */
  favoriten?: FavoritEintrag[];
  workflow?: TrialWorkflowResult;
};

function formatStatusLabel(status: TrialStatus): string {
  switch (status) {
    case 'scheduled':
      return 'Termin anstehend';
    case 'started':
      return 'Kurs begonnen';
    case 'completed':
      return 'Termin durchgeführt';
    case 'no_show':
      return 'Nicht erschienen';
    case 'cancelled':
      return 'Termin abgesagt';
    case 'rescheduled':
      return 'Termin verschoben';
    case 'offer_sent':
      return 'Angebot versendet';
    default:
      return status;
  }
}

const WORKFLOW_STEPS: Array<{ key: TrialWorkflowState; label: string }> = [
  { key: 'probestunde', label: 'Probestunde' },
  { key: 'entscheidung', label: 'Entscheidung' },
  { key: 'anmeldung', label: 'Anmeldung' },
  { key: 'angebot', label: 'Angebot' },
  { key: 'unterricht', label: 'Unterricht' },
];

function workflowActionVariantToButton(variant: 'primary' | 'secondary' | 'destructive'): 'cta' | 'secondary' | 'destructive' {
  if (variant === 'primary') return 'cta';
  if (variant === 'destructive') return 'destructive';
  return 'secondary';
}

export default function TrialLessonGridCard({
  trial,
  subjectLabel,
  dashboardCompact = false,
  onReschedule,
  onCancel,
  onMarkOccurred,
  onResendConfirmation,
  onNoOccurrence,
  onEnrollment,
  onConsiderUntil,
  onNotStarted,
  onDeleteEnrollment,
  onCancelTeaching,
  onOrderConfirm,
  onSendOffer,
  onStartCourse,
  onReminder,
  onEmail,
  onOpenHistory,
  onAddQuickNote,
  onContactAction,
  onToggleFavorite,
  isFavorite = false,
  favoriten,
  workflow,
}: Props) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const [workflowMoreOpen, setWorkflowMoreOpen] = useState(false);
  const offerHonorarCheck = useTrialOfferHonorarMissing(
    trial,
    workflow?.state === 'angebot' && !!onSendOffer,
  );
  const [quickNote, setQuickNote] = useState('');
  const [savingQuickNote, setSavingQuickNote] = useState(false);

  const studentLink = useMemo(() => {
    const subId = (trial.subprofileId || '').trim();
    if (!subId || !subId.includes('-')) return null;
    const parts = subId.split('-');
    const parentId = parts[0];
    const suffix = parts[parts.length - 1] || '1';
    return `/dashboard/people/${parentId}/sub/${suffix}`;
  }, [trial.subprofileId]);

  const timeLabel = formatTimeRangeFromStrings(trial.startTime || '', trial.endTime || '');
  const subject = (trial.fachName || trial.instrument || '').trim();
  const tel = (trial.contactPhone || '').trim() ? `tel:${trial.contactPhone}` : '';

  const handleSaveQuickNote = async (e: React.SyntheticEvent) => {
    stop(e);
    const text = quickNote.trim();
    if (!text || !onAddQuickNote) return;
    setSavingQuickNote(true);
    try {
      await onAddQuickNote(text);
      setQuickNote('');
      toast.success('Kommentar gespeichert');
    } catch (error: any) {
      toast.error(error?.message || 'Kommentar konnte nicht gespeichert werden');
    } finally {
      setSavingQuickNote(false);
    }
  };

  const executeWorkflowAction = (actionId: TrialWorkflowActionId, e: React.SyntheticEvent) => {
    stop(e);
    switch (actionId) {
      case 'mark_occurred':
        onMarkOccurred?.();
        return;
      case 'resend_confirmation':
        onResendConfirmation?.();
        return;
      case 'reschedule':
        onReschedule?.();
        return;
      case 'cancel':
        onCancel?.();
        return;
      case 'cancel_teaching':
        onCancelTeaching?.();
        return;
      case 'consider_until':
        onConsiderUntil?.();
        return;
      case 'mark_no_occurrence':
        onNoOccurrence?.();
        return;
      case 'send_enrollment':
      case 'edit_enrollment':
        onEnrollment?.();
        return;
      case 'mark_not_started':
        onNotStarted?.();
        return;
      case 'send_reminder':
        // Nicht ?? verwenden: onReminder liefert undefined, dann liefe onEmail
        // hinterher und überschriebe die Erinnerung mit der leeren Nachricht.
        if (onReminder) onReminder();
        else onEmail?.();
        return;
      case 'send_offer':
        onSendOffer?.();
        return;
      case 'confirm_teaching':
        onOrderConfirm?.();
        return;
      case 'start_course':
        onStartCourse?.();
        return;
      default:
        return;
    }
  };

  const isWorkflowActionAvailable = (actionId: TrialWorkflowActionId): boolean => {
    switch (actionId) {
      case 'mark_occurred':
        return !!onMarkOccurred;
      case 'resend_confirmation':
        return !!onResendConfirmation;
      case 'reschedule':
        return !!onReschedule;
      case 'cancel':
        return !!onCancel;
      case 'cancel_teaching':
        return !!onCancelTeaching;
      case 'consider_until':
        return !!onConsiderUntil;
      case 'mark_no_occurrence':
        return !!onNoOccurrence;
      case 'send_enrollment':
      case 'edit_enrollment':
        return !!onEnrollment;
      case 'mark_not_started':
        return !!onNotStarted;
      case 'send_reminder':
        return !!onReminder || !!onEmail;
      case 'send_offer':
        return !!onSendOffer;
      case 'confirm_teaching':
        return !!onOrderConfirm;
      case 'start_course':
        return !!onStartCourse;
      default:
        return false;
    }
  };

  return (
    <div
      className={cn(
        'bg-card text-card-foreground rounded-2xl shadow-sm border border-border p-4',
        'space-y-4'
      )}
    >
      {workflow ? (
        <div className="flex flex-wrap gap-2 border-b border-border pb-3">
          {WORKFLOW_STEPS.map((step, index) => {
            // Läuft der Unterricht, ist auch die letzte Stufe abgehakt.
            const done = workflow.courseStarted || index < workflow.stepIndex;
            const active = !done && index === workflow.stepIndex;
            return (
              <div
                key={step.key}
                className={cn(
                  'inline-flex min-w-[120px] items-center justify-center rounded-full px-3 py-1 text-xs font-semibold',
                  done ? 'bg-primary/90 text-primary-foreground' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                )}
              >
                {done ? `✓ ${step.label}` : step.label}
              </div>
            );
          })}
        </div>
      ) : null}
      <div
        className={cn(
          'grid gap-4 items-start',
          // Dashboard-Compact bleibt bewusst einspaltig, um Overflow in schmalen Widgets zu vermeiden.
          dashboardCompact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-trialLessonCard'
        )}
      >
      {/* Teilnehmer + Termin + Lehrer */}
      <div className="space-y-2">
        {/* Gleicher Stern wie bei den Anfragen, mit den Namen (T-0014). */}
        {onToggleFavorite || (favoriten && favoriten.length > 0) ? (
          <FavoritStern
            istFavorit={!!isFavorite}
            favoriten={favoriten}
            onToggle={onToggleFavorite ? () => onToggleFavorite() : undefined}
          />
        ) : null}
        <div className="flex items-center gap-2">
          {subject ? (
            <span className="inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
              {subject}
            </span>
          ) : subjectLabel ? (
            <span className="inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
              Probestunde
            </span>
          ) : null}
        </div>

        <div className="space-y-1">
          <div className="text-sm font-semibold leading-tight">
            {studentLink ? (
              <a className="hover:underline" href={studentLink} onClick={(e) => e.stopPropagation()}>
                {trial.studentName}
              </a>
            ) : (
              trial.studentName
            )}
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5">
            <div>{trial.contactEmail || '—'}</div>
            <div>{trial.contactPhone || '—'}</div>
          </div>
        </div>
        <div className="pt-1 space-y-2">
          <div className="text-xs font-semibold text-muted-foreground">Termin</div>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{trial.date ? formatDate(trial.date) : '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{timeLabel}</span>
            </div>
            {/* Verschoben, abgesagt, durchgeführt — vorher nirgends zu sehen (T-0167, T-0168). */}
            <div className="text-xs font-medium">{formatStatusLabel(trial.status)}</div>
            {trial.roomName ? (
              <div className="text-xs text-muted-foreground">Raum: {trial.roomName}</div>
            ) : null}
            <div className="pt-1">
              <span className="text-xs text-muted-foreground">Lehrer: </span>
              <span className="text-sm font-medium">{trial.teacherName || '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Kontakt */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">Kontakt</div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg"
            disabled={!tel}
            onClick={(e) => {
              stop(e);
              if (tel) {
                window.open(tel);
                void onContactAction?.('phone');
              }
            }}
            title="Anrufen"
          >
            <Phone className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg text-destructive"
            onClick={(e) => {
              stop(e);
              void onContactAction?.('call_failed');
            }}
            title="Nicht erreicht"
          >
            <PhoneOff className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg"
            disabled={!onEmail}
            onClick={(e) => {
              stop(e);
              onEmail?.();
              void onContactAction?.('email');
            }}
            title="Nachricht senden (E-Mail/SMS)"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Historie */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">Ereignisse</div>
        <Button
          type="button"
          variant="outline"
          className="h-auto w-full min-w-0 justify-start overflow-hidden rounded-lg px-3 py-2 text-left"
          disabled={!onOpenHistory}
          onClick={(e) => {
            stop(e);
            onOpenHistory?.();
          }}
        >
          {/* Der Eintrag ist ein Fließtext beliebiger Länge — hier nur anreißen,
              sonst läuft er aus der Karte in die Aktionen daneben. */}
          <div className="min-w-0 w-full">
            <div className="text-xs text-muted-foreground">Klicken, um Historie zu öffnen</div>
            <div className="mt-1 line-clamp-2 break-words text-xs text-foreground">
              {trial.latestHistoryDescription?.trim()
                ? trial.latestHistoryDescription
                : `Geplante Probestunde · ${trial.date ? formatDate(trial.date) : '—'}`}
            </div>
          </div>
        </Button>
        <div className="flex gap-2 items-center">
          <Input
            className="max-w-[320px] flex-1"
            placeholder="Kommentar hinzufügen"
            value={quickNote}
            onChange={(e) => setQuickNote(e.target.value)}
            onClick={stop}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (!quickNote.trim() || !onAddQuickNote || savingQuickNote) return;
                void handleSaveQuickNote(e);
              }
            }}
          />
          <Button
            type="button"
            className="h-10 w-10 p-0 rounded-lg"
            disabled={!onAddQuickNote || !quickNote.trim() || savingQuickNote}
            onClick={handleSaveQuickNote}
            title="Kommentar speichern"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {workflow ? (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-center">
              <div className="text-[11px] font-semibold text-muted-foreground">Anmeldung</div>
              <div className={cn('text-xs', workflow.anmeldungStatus === 'abgeschlossen' ? 'text-green-600 font-semibold' : 'text-muted-foreground')}>
                {workflow.anmeldungStatus}
              </div>
            </div>
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-center">
              <div className="text-[11px] font-semibold text-muted-foreground">Angebot</div>
              <div
                className={cn(
                  'text-xs',
                  workflow.angebotStatus === 'abgeschlossen'
                    ? 'text-green-600 font-semibold'
                    : workflow.angebotStatus === 'abgelehnt'
                      ? 'text-red-600 font-semibold'
                      : 'text-muted-foreground'
                )}
              >
                {workflow.angebotStatus}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Status + Quick Actions */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {workflow ? (
            <>
              {workflow.actions
                // Ohne Handler wäre „Unterricht absagen“ ein toter Knopf (T-0072).
                .filter((action) => action.id !== 'cancel_teaching' || !!onCancelTeaching)
                .map((action) => (
                <Button
                  key={action.id}
                  type="button"
                  variant={workflowActionVariantToButton(action.variant)}
                  className="justify-start"
                  disabled={
                    !isWorkflowActionAvailable(action.id)
                    || (action.id === 'send_offer' && (offerHonorarCheck.loading || offerHonorarCheck.missing))
                  }
                  title={
                    action.id === 'send_offer' && offerHonorarCheck.missing
                      ? offerHonorarCheck.message
                      : undefined
                  }
                  onClick={(e) => executeWorkflowAction(action.id, e)}
                >
                  {action.label}
                </Button>
              ))}
              {workflow.moreActions.length > 0 ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start"
                    onClick={(e) => {
                      stop(e);
                      setWorkflowMoreOpen((prev) => !prev);
                    }}
                  >
                    {workflowMoreOpen ? 'weitere Aktionen ausblenden' : 'weitere Aktionen einblenden'}
                  </Button>
                  {workflowMoreOpen ? (
                    <div className="flex flex-col gap-2">
                      {workflow.moreActions.map((action) => (
                        <Button
                          key={action.id}
                          type="button"
                          variant={workflowActionVariantToButton(action.variant)}
                          className="justify-start"
                          disabled={!isWorkflowActionAvailable(action.id)}
                          onClick={(e) => executeWorkflowAction(action.id, e)}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          {!workflow ? (
            <>
          {/* Anstehend/Verschoben: Reihenfolge 1) Verschieben 2) Absagen 3) Bestätigung erneut versenden */}
          {onReschedule && !onEnrollment && !onNotStarted && !onConsiderUntil && !onNoOccurrence ? (
            <Button
              type="button"
              variant="secondary"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onReschedule();
              }}
            >
              <CalendarClock className="h-4 w-4 mr-2" />
              Verschieben
            </Button>
          ) : null}
          {onCancel && !onEnrollment && !onNotStarted && !onConsiderUntil && !onNoOccurrence ? (
            <Button
              type="button"
              variant="destructive"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onCancel();
              }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Absagen
            </Button>
          ) : null}
          {onResendConfirmation && !onEnrollment && !onNotStarted && !onConsiderUntil && !onNoOccurrence ? (
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onResendConfirmation();
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              Bestätigung erneut versenden
            </Button>
          ) : null}

          {/* Durchgeführt: Reihenfolge 1-6 */}
          {onOrderConfirm ? (
            <Button
              type="button"
              variant="cta"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onOrderConfirm();
              }}
            >
              <Check className="h-4 w-4 mr-2" />
              Bestätigen
            </Button>
          ) : null}
          {onEnrollment && !onOrderConfirm ? (
            <Button
              type="button"
              variant="cta"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onEnrollment();
              }}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Anmelden
            </Button>
          ) : null}
          {onNotStarted && !onOrderConfirm ? (
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onNotStarted();
              }}
            >
              <UserX className="h-4 w-4 mr-2" />
              Fängt nicht an
            </Button>
          ) : null}
          {onDeleteEnrollment && !onOrderConfirm ? (
            <Button
              type="button"
              variant="outline"
              className="justify-start text-destructive"
              onClick={(e) => {
                stop(e);
                onDeleteEnrollment();
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Anmeldung löschen
            </Button>
          ) : null}
          {onConsiderUntil && !onOrderConfirm ? (
            <Button
              type="button"
              variant="secondary"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onConsiderUntil();
              }}
            >
              <Clock3 className="h-4 w-4 mr-2" />
              Überlegt bis …
            </Button>
          ) : null}
          {onNoOccurrence && !onOrderConfirm ? (
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onNoOccurrence();
              }}
            >
              <CalendarX2 className="h-4 w-4 mr-2" />
              Termin hat nicht stattgefunden
            </Button>
          ) : null}
          {onReschedule && (onOrderConfirm || onEnrollment || onNotStarted || onConsiderUntil || onNoOccurrence) ? (
            <Button
              type="button"
              variant="secondary"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onReschedule();
              }}
            >
              <CalendarClock className="h-4 w-4 mr-2" />
              Verschieben
            </Button>
          ) : null}
          {onCancel && (onOrderConfirm || onEnrollment || onNotStarted || onConsiderUntil || onNoOccurrence) ? (
            <Button
              type="button"
              variant="destructive"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onCancel();
              }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Absagen
            </Button>
          ) : null}

          {/* Fallback für seltene Mischzustände */}
          {onResendConfirmation && (onOrderConfirm || onEnrollment || onNotStarted || onConsiderUntil || onNoOccurrence) ? (
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={(e) => {
                stop(e);
                onResendConfirmation();
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              Bestätigung erneut versenden
            </Button>
          ) : null}
          {onReschedule || onCancel || onResendConfirmation || onNoOccurrence || onEnrollment || onConsiderUntil || onNotStarted || onOrderConfirm ? null : (
            <div className="text-xs text-muted-foreground">Keine Aktionen verfügbar</div>
          )}
            </>
          ) : null}
        </div>
      </div>
      </div>

    </div>
  );
}


